import { useCallback } from 'react';
import { SpeechToTextAction } from '@unifyapps/carbon/components/Dictation/SpeechToTextAction';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { useDictation } from '@unifyapps/carbon/components/Dictation/useDictation';
import type { InputSizes } from '@unifyapps/ui/components/Input';
import type {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
} from '@unifyapps/defs/types/blocks/button';

type Props = {
  suffixIcon?: {
    name?: string;
  };
  allowDictation?: boolean;
  input?: string;
  locale?: string;
  onInputChange: (newInput: string) => void;
  inputRef: React.MutableRefObject<HTMLElement | null>;
  methodsRef?: React.MutableRefObject<{
    onInputChange?: (newInput: string | undefined) => void;
  } | null>;
  size?: InputSizes;
  dictation?: {
    appearance?: {
      color?: ButtonColors;
      size?: ButtonSizes;
      variant?: ButtonVariants;
    };
  };
};

export const useEndDecorator = ({
  suffixIcon,
  allowDictation,
  input,
  onInputChange,
  inputRef,
  methodsRef,
  size,
  dictation,
  locale,
}: Props) => {
  const SuffixIcon = getIconFromRegistry(suffixIcon?.name);

  const { showMicrophone, hideMicrophone, onSpeechTimeout } = useDictation({
    input,
    allowDictation: Boolean(allowDictation),
    inputRef,
    methodsRef,
  });

  const onStartDictation = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const endDecoratorNode = showMicrophone ? (
    <>
      {SuffixIcon ? (
        <SuffixIcon height={size === 'sm' ? 14 : 16} width={size === 'sm' ? 14 : 16} />
      ) : null}
      <SpeechToTextAction
        className={SuffixIcon ? 'ml-md' : undefined}
        color={dictation?.appearance?.color ?? 'brand'}
        disabled={false}
        input={input}
        isLoading={false}
        language={locale}
        onInputChange={onInputChange}
        onSpeechTimeout={onSpeechTimeout}
        onStartDictation={onStartDictation}
        onStopListening={hideMicrophone}
        size={dictation?.appearance?.size ?? 'sm'}
        variant={dictation?.appearance?.variant ?? 'outline'}
      />
    </>
  ) : null;

  return {
    endDecoratorNode,
    endDecoratorComponent: showMicrophone ? undefined : SuffixIcon,
  };
};
